<script lang="ts">
  /**
   * 課題中テーマ選択 UI。 大カテゴリ配下のテーマ候補だけを表示する。
   */
  import { problemThemesApi } from '@shared/api/problem-themes';
  import type { ProblemTheme } from '@shared/types/problem-theme';

  interface Props {
    category: string;
    value: string;
    onChange: (value: string) => void | Promise<void>;
    disabled?: boolean;
    allowAdd?: boolean;
    placeholderLabel?: string;
  }

  let {
    category,
    value,
    onChange,
    disabled = false,
    allowAdd = false,
    placeholderLabel = '(未設定)',
  }: Props = $props();

  const ADD_SENTINEL = '__add_theme__';

  let themes = $state<ProblemTheme[]>([]);
  let loading = $state(false);
  let loadError = $state<string | null>(null);

  let adding = $state(false);
  let newName = $state('');
  let saving = $state(false);
  let changing = $state(false);
  let addError = $state<string | null>(null);
  let changeError = $state<string | null>(null);
  let selected = $state(value);
  let loadToken = 0;

  $effect(() => {
    if (!changing) selected = value;
  });

  $effect(() => {
    const nextCategory = category;
    queueMicrotask(() => {
      void loadThemes(nextCategory);
    });
  });

  async function loadThemes(nextCategory: string): Promise<void> {
    const token = ++loadToken;
    const trimmed = nextCategory.trim();
    if (trimmed === '') {
      themes = [];
      loading = false;
      loadError = null;
      adding = false;
      return;
    }

    loading = true;
    loadError = null;
    try {
      const res = await problemThemesApi.list(trimmed);
      if (token !== loadToken) return;
      themes = res.items;
    } catch (err) {
      if (token !== loadToken) return;
      loadError = err instanceof Error ? err.message : String(err);
      themes = [];
    } finally {
      if (token === loadToken) loading = false;
    }
  }

  async function applyChange(next: string): Promise<void> {
    if (changing) return;
    const previous = value;
    selected = next;
    changing = true;
    changeError = null;
    try {
      await onChange(next);
    } catch (err) {
      selected = previous;
      changeError = err instanceof Error ? err.message : String(err);
    } finally {
      changing = false;
    }
  }

  function handleSelect(ev: Event): void {
    const next = (ev.currentTarget as HTMLSelectElement).value;
    if (next === ADD_SENTINEL) {
      selected = value;
      adding = true;
      addError = null;
      changeError = null;
      newName = '';
      return;
    }
    void applyChange(next);
  }

  function cancelAdd(): void {
    adding = false;
    newName = '';
    addError = null;
  }

  async function submitAdd(): Promise<void> {
    if (saving) return;
    const trimmedCategory = category.trim();
    const trimmedName = newName.trim();
    if (trimmedCategory === '') {
      addError = '先に大カテゴリを選択してください';
      return;
    }
    if (trimmedName === '') {
      addError = '中テーマを入力してください';
      return;
    }
    saving = true;
    addError = null;
    try {
      const created = await problemThemesApi.create(
        trimmedCategory,
        trimmedName,
      );
      await loadThemes(trimmedCategory);
      adding = false;
      newName = '';
      await applyChange(created.name);
    } catch (err) {
      addError = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  function onAddKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void submitAdd();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      cancelAdd();
    }
  }
</script>

<div class="theme-select">
  {#if adding}
    <div class="add-row">
      <input
        type="text"
        bind:value={newName}
        placeholder="新しい中テーマ"
        autocomplete="off"
        disabled={saving}
        onkeydown={onAddKey}
      />
      <button
        type="button"
        class="btn btn-primary"
        disabled={saving}
        onclick={submitAdd}
      >
        {saving ? '追加中…' : '追加'}
      </button>
      <button type="button" class="btn" disabled={saving} onclick={cancelAdd}
        >キャンセル</button
      >
    </div>
    {#if addError}<small class="error">{addError}</small>{/if}
  {:else}
    <select
      bind:value={selected}
      disabled={disabled || changing || category.trim() === ''}
      onchange={handleSelect}
    >
      <option value="">
        {category.trim() === ''
          ? '大カテゴリを選択'
          : loading
            ? '読み込み中…'
            : placeholderLabel}
      </option>
      {#each themes as theme (theme.name)}
        <option value={theme.name}>{theme.name}</option>
      {/each}
      {#if allowAdd && category.trim() !== ''}
        <option value={ADD_SENTINEL}>+ 中テーマを追加</option>
      {/if}
    </select>
    {#if changing}
      <small class="hint">保存中…</small>
    {:else if loadError}
      <small class="error">{loadError}</small>
    {:else if changeError}
      <small class="error">{changeError}</small>
    {/if}
  {/if}
</div>

<style>
  .theme-select {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .add-row {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
  }
  .add-row input {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
  }
  small.hint,
  small.error {
    font-size: 0.75rem;
  }
  small.hint {
    color: #6b7280;
  }
  small.error {
    color: #b91c1c;
  }
</style>
